import React from 'react';

import { V1DiscountDetail } from '@/api/clients/ohip';
import { IRoomAvailabilityInstance } from '@/availability/models/room-availability.model';
import { RoomRateCodeSelector } from '@/booking/components/RoomRateCodeSelector.component';
import { useActiveBrandConfig } from '@/brand';
import { getNumberOfNightsBetween } from '@/common/utils/date';
import type { IFinancialAmountInstance } from '@/finance';
import { useExperimentStatus } from '@/flags/use-experiments';
import type { HotelConfiguration } from '@/hotel';
import { calculateAverageNightlyRateNet } from '@/hotel/helpers/calculate-average-nightly-rate-net';

import { IHotelModelInstance, IStayCostInstance } from '../models';
import { HotelRoomListItemDiscountedRate } from './HotelRoomListItemDiscountedRate.component';
import { RoomTotal } from './room/RoomTotal.component';

export interface RoomRateProp {
  code: string;
  name: string;
  description?: string;
  isAvailable: boolean;
  totalWithTaxes: IFinancialAmountInstance | undefined;
  isTotalInclusiveOfAllTaxes: boolean;
  netPrice: IFinancialAmountInstance | undefined;
  includedTaxesMessageTranslationKey: HotelConfiguration['legacyTaxes']['includedTaxesMessageTranslationKey'];
  // I'm not sold on how this is implemented in this component at all...
  priceBeforeDiscount: IFinancialAmountInstance | undefined;
  // If this is true, it's deemed we're in a 'discount mode'. We should only render one, discounted, rate code.
  isDiscountRate: boolean;
  averageNightlyPrice: IFinancialAmountInstance | undefined;
  containsFees: boolean;
  discountDetail: V1DiscountDetail;
}

interface HotelRoomListItemRatesProps {
  roomAvailabilityModel: IRoomAvailabilityInstance;
  onSelectRoomRate: (rateCode: string) => void;
  onRequestRoomSelectionReset: () => void;
  selectedRequestRateCode: string;
  isRateSwitchingLocked: boolean;
  hotel: IHotelModelInstance;
  checkInDate: string;
  checkOutDate: string;
}

/**
 * Either renders a rate code selection component, or a single rate if there's only one.
 * @TODO Clean up this logic. We loop through and create some weird arbitrary array (roomRates).
 * @param props
 */
export const HotelRoomListItemRates = ({
  roomAvailabilityModel,
  onSelectRoomRate,
  onRequestRoomSelectionReset,
  selectedRequestRateCode,
  isRateSwitchingLocked,
  hotel,
  checkInDate,
  checkOutDate,
}: HotelRoomListItemRatesProps) => {
  const { showAveragePricePerNight } = useActiveBrandConfig();

  const showDisloyaltyMembershipRate = useExperimentStatus(
    'feature-show-disloyalty-membership-rate'
  );

  const { legacyTaxes: taxes } = hotel;
  const roomRates = roomAvailabilityModel.availability.map(
    (stayCost: IStayCostInstance): RoomRateProp => {
      const netPrice = stayCost.getDisplayableTotalRoomRate(
        taxes.displayRateWithVat
      );

      // Fetch the rate code for the rate with the DEFAULT type ('DEFAULT' in this case means the API has included it without being asked.
      // Do not confuse this with the actual default selected rate code.
      // The DEFAULT rate type in this context is the 'before discount' rate code price).
      const priceBeforeDiscount =
        roomAvailabilityModel.rateCodeBeforeDiscount?.getDisplayableTotalRoomRate(
          taxes.displayRateWithVat
        ) ?? undefined;

      const averageNightlyRateNet = calculateAverageNightlyRateNet(
        hotel.referenceId,
        netPrice,
        checkInDate,
        checkOutDate,
        taxes
      );

      return {
        code: stayCost.rateCode.requestRateCode,
        name: stayCost.rateCode.nameOrDefault,
        description: stayCost.rateCode.descriptionOrDefault,
        isAvailable: Boolean(stayCost.bookable.value),
        netPrice,
        totalWithTaxes: stayCost.grandTotal,
        // If we're requesting advanced taxes, render a message with final total value
        isTotalInclusiveOfAllTaxes: !taxes.isAdvanced,
        includedTaxesMessageTranslationKey:
          taxes.includedTaxesMessageTranslationKey,
        priceBeforeDiscount,
        isDiscountRate: roomAvailabilityModel.isRoomAvailabilityDiscounted,
        averageNightlyPrice:
          showAveragePricePerNight &&
          getNumberOfNightsBetween(checkInDate, checkOutDate) > 1
            ? averageNightlyRateNet ?? stayCost.averageNightlyRate
            : undefined,
        containsFees: stayCost.containsFees,
        discountDetail: stayCost.discountDetail,
      };
    }
  );
  const discountRate = roomRates.find((rate) => rate.isDiscountRate);

  if (discountRate) {
    if (!discountRate.netPrice) {
      throw new Error(
        `Missing netPrice from discount rate [${discountRate.code}] inside HotelRoomListItemRates`
      );
    }

    if (!discountRate.priceBeforeDiscount) {
      throw new Error(
        `Missing priceBeforeDiscount from discount rate [${discountRate.code}] inside HotelRoomListItemRates`
      );
    }

    return (
      <HotelRoomListItemDiscountedRate
        priceBeforeDiscount={discountRate.priceBeforeDiscount}
        name={discountRate.name}
        description={discountRate.description}
        isAvailable={discountRate.isAvailable}
        roomTotalElement={
          <RoomTotal
            totalWithTaxes={discountRate.totalWithTaxes}
            isTotalInclusiveOfAllTaxes={discountRate.isTotalInclusiveOfAllTaxes}
            price={discountRate.netPrice}
            includedTaxesMessageTranslationKey={
              discountRate.includedTaxesMessageTranslationKey
            }
            taxLabel={taxes.taxLabel}
          />
        }
      />
    );
  }

  // Feature flagging the Dis-loyalty "Teleport" rate.
  // If the flag is disabled, filter out all membership rates.
  const filteredRoomRates = showDisloyaltyMembershipRate
    ? roomRates
    : roomRates.filter((rate) => !rate.discountDetail.membershipRateCode);

  if (!filteredRoomRates.length) return null;

  return (
    <RoomRateCodeSelector
      roomRates={filteredRoomRates}
      onSelectRoomRate={onSelectRoomRate}
      onRequestRoomSelectionReset={onRequestRoomSelectionReset}
      selectedRateCode={selectedRequestRateCode}
      isRateSwitchingLocked={isRateSwitchingLocked}
      hotel={hotel}
    />
  );
};

export default HotelRoomListItemRates;
